// What the voice agents actually captured — the leads, the bookings, the
// messages taken down for someone to call back.
//
// This is a different question from the one Call Activity answers, and they are
// deliberately not the same screen. Call Activity reads agent_call_events: one
// row per finished CALL, and what the post-call pipeline then did with it — the
// mechanics. This reads agent_captures: one row per THING CAPTURED, with the
// caller's own answers in it. A single call can produce a lead and then a
// booking; a call that produced nothing has no row here at all. "Which calls ran
// and did the sync work" and "what did we get" are questions different people
// ask on different days.
//
// WHY THERE IS NO PER-CALL GROUPING HERE, and no per-call route.
//
// GET /v1/captures/call/{callSid} used to answer [] for every call ever placed,
// because no capture tool payload carried a call id. That changed: the tools now
// send call_sid and rows carry it, so a capture links to the call that produced
// it and each row offers that link.
//
// What is STILL not done here is stitching a capture to a call by matching phone
// number and timestamp when the id is absent. That would invent a link the data
// does not contain, and would attach the wrong lead to the wrong call the first
// time somebody rang twice. Rows captured before the tools sent an id therefore
// say so instead of guessing. The grouping offered remains by contact,
// newest first. Each such row states plainly that it is not tied to a call, rather
// than leaving a blank where a call id should be.
//
// ON THE FIELD VALUES. The keys are the business's vocabulary, never ours — a
// wellness retreat sends primary_wellness_goal and expected_checkin_month, a
// salon sends neither — so nothing here enumerates, orders or filters by field
// name. That includes the sensitive ones: health_notes is in one live tool
// schema, and it is NOT special-cased, because a rule that hides "health-ish"
// key names is exactly the per-business hardcode this design forbids, and it
// would fail open on the next customer whose sensitive field is called
// something else. What protects the payload is structural instead: the values
// live behind a per-row disclosure rather than on the list, so nothing free-text
// is on screen until someone opens one row; and AgentCaptureController checks
// the requested organisation against the caller's accessible ones and answers
// 403 — unlike ContactController, which trusts the header it is handed.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  RefreshCw, Loader2, AlertCircle, User, ArrowLeft, CalendarClock, Link2, X, PhoneOff, Play, ExternalLink, MessageSquare, Download,
  SlidersHorizontal, ChevronDown, ChevronUp, Pencil, Plus,
} from 'lucide-react';
import Pagination from 'components/Common/Pagination';
import LeadSummary from './LeadSummary';
import { useAuth } from 'hooks/useAuth';
import AudioSessionModal from 'components/Modals/AudioSessionModal';
import ContactPeekModal from 'components/Contacts/ContactPeekModal';
import { sessionsService } from 'api/services/sessions.service';
import { recordingsService } from 'api/services/recordings.service';
import { agentCallsService } from 'api/services/crmProviders.service';
import TranscriptViewer from 'components/Common/TranscriptViewer';
import AudioPlayer from 'components/Common/AudioPlayer';
import { capturesService, Capture, CapturePage, EMPTY_PAGE } from 'api/services/captures.service';
import { aiAgentsService, AiAgent } from 'api/services/aiAgents.service';
import LeadEditModal from './LeadEditModal';
import { canEditLeads, isEditableCapture } from './leadEditRules';
import {
  captureFieldRows, captureFields, captureSubject, formatCapturedAt, captureHeadline, captureIdentity,
  amendedBookingRef, captureTypeMeta, syncMeta, attentionCount, CAPTURE_TYPE_OPTIONS,
} from './captureFields';
import {
  CaptureFilters, EMPTY_FILTERS, SYNC_STATUS_OPTIONS, DID_NONE, DidProbe,
  captureQueryParams, hasAnyFilter, isSpecificDid, isBackwardsRange,
  agentsById, agentLabel, agentOptions, activeFilterChips, withoutFilter,
  filtersToSearchParams, filtersFromSearchParams, emptyReason,
  CAPTURE_VIEWS, isViewActive, CAPTURE_TABS,
} from './captureFilters';

const PAGE_SIZE = 20;

const KNOWN_TYPES = CAPTURE_TYPE_OPTIONS.map((o) => o.value);

/** One capture, closed. Opening it is what reveals the caller's answers. */
/**
 * Everything a capture says, wherever it is being shown.
 *
 * Extracted so one body serves two placements: inline under the row on a phone,
 * and in the side panel on a wide screen. Rendering it twice from one source is
 * the only way the two cannot drift — a panel that slowly stopped matching the
 * expanded row would be worse than not having one.
 */
const CaptureDetail: React.FC<{
  capture: Capture;
  agentNames: Map<number, string>;
  showContactLink: boolean;
  onOpenCall: (callSid: string) => void;
  openingCall: string | null;
  /**
   * Save the call's audio without opening it first.
   *
   * <p>Beside the play link rather than only inside the dialog it opens: the
   * side panel is where somebody reads a lead and decides they want the call,
   * and making them open a player to reach a Download is a click that answers
   * no question.
   */
  onDownloadCall: (callSid: string) => void;
  downloadingCall: string | null;
  /**
   * How much width this body has, which its container knows and it cannot.
   *
   * Tailwind's `sm:` keys off the VIEWPORT, so a two-column field grid inside a
   * 23rem side panel still thought it was on a wide screen: a w-40 label left
   * almost nothing beside it and "Hindi" wrapped to one letter per line. The
   * placement passes this in rather than the body guessing.
   */
  width?: 'wide' | 'medium' | 'narrow';
  /**
   * Opens the edit form. Absent when the caller's role may not edit leads; the
   * button is also absent on every capture that is not a LEAD, because a
   * booking's state belongs to whoever holds the appointment.
   */
  onEdit?: (capture: Capture) => void;
}> = ({ capture, agentNames, showContactLink, onOpenCall, openingCall,
        onDownloadCall, downloadingCall, width = 'wide', onEdit }) => {
  // Two decisions, not three sizes: how many columns of fields, and whether a
  // label can sit beside its value. 'medium' is the half-page panel — room for
  // a label beside its value, but not for two of those pairs side by side.
  const narrow = width === 'narrow';
  const twoColumn = width === 'wide';
  const type = captureTypeMeta(capture.captureType);
  const { answered, blank } = captureFieldRows(capture);
  const editable = !!onEdit && isEditableCapture(capture);
  return (
    <div className="space-y-3">
        {editable && (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="tl-btn tl-btn-sm tl-btn-secondary"
              onClick={() => onEdit!(capture)}
              title="Correct the name, notes, status or captured answers"
            >
              <Pencil className="w-3.5 h-3.5" /> Edit
            </button>
          </div>
        )}
        {/* THE ACTIONS COME FIRST.
            They used to close the panel, sharing a wrapping row with the
            `source · agent · line` provenance — so on a capture with a dozen
            answered fields, a flagged value and a sync note, Play and Download
            sat below all of it inside a rail that is itself capped at
            calc(100vh-9rem) and scrolls. You had to scroll a side panel to find
            the two things you open a call to do.

            Real buttons rather than 11px text links, and the same pill the rest
            of the page uses: on a phone this body is the expanded card, where a
            row of underlined 11px text is not a touch target. They wrap to a
            second line before they shrink. */}
        {capture.callSid && (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="tl-btn tl-btn-sm tl-btn-secondary"
              onClick={() => onOpenCall(capture.callSid!)}
              disabled={openingCall === capture.callSid}
              title={capture.callSid}
            >
              {openingCall === capture.callSid
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <Play className="w-3.5 h-3.5" />}
              {openingCall === capture.callSid ? 'Opening…' : 'Play the recording'}
            </button>
            {/* Offered without first checking that a recording exists — that
                check is a request per row, and most rows are never opened. If
                there is none, the handler says so rather than saving nothing. */}
            <button
              type="button"
              className="tl-btn tl-btn-sm tl-btn-neutral"
              onClick={() => onDownloadCall(capture.callSid!)}
              disabled={downloadingCall === capture.callSid}
              title="Save this call's audio"
            >
              {downloadingCall === capture.callSid
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <Download className="w-3.5 h-3.5" />}
              {downloadingCall === capture.callSid ? 'Preparing…' : 'Download'}
            </button>
          </div>
        )}

        <p className="text-[12px]" style={{ color: 'var(--text-muted)' }}>{type.description}</p>

        {answered.length > 0 && (
          <dl className={twoColumn ? 'grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5' : 'space-y-1.5'}>
            {answered.map((f) => (
              /* Side by side where there is room; stacked where there is not.
                 A fixed-width label in a narrow rail is what reduced values to
                 a single character per line. */
              <div key={f.key}
                   className={narrow
                     ? 'text-[12px] min-w-0'
                     : 'sm:flex sm:gap-2 text-[12px] min-w-0'}>
                {/* `sm:` and not a bare w-40. This branch is what the EXPANDED
                    CARD uses — the only detail body below lg, and on a phone it
                    is ~295px wide, where a 160px fixed label left 135px for the
                    value: the same one-letter-per-line fault the `narrow` mode
                    was added to fix, just at a different width.

                    Keying off the viewport is safe HERE and nowhere else: this
                    body spans the screen, so viewport and container are the same
                    thing. The side rail passes width="medium" and only exists at
                    lg, where these always apply. */}
                <dt className={`font-medium text-gray-700 dark:text-gray-200 ${narrow ? '' : 'sm:w-40 sm:shrink-0'}`}>
                  {f.label}
                </dt>
                <dd className="min-w-0 break-words" style={{ color: 'var(--text-muted)' }}>{f.value}</dd>
              </div>
            ))}
          </dl>
        )}

        {/* Flagged, not corrected and not hidden. A lead reached Zoho with its
            phone set to the literal string {call_from} and was rejected for an
            invalid phone; nothing between the agent and the CRM had noticed.
            The row still syncs — blocking would keep the CRM clean at the cost
            of losing the lead — so this says what to look at and leaves the
            decision with a person. */}
        {(capture.dataFlags?.length ?? 0) > 0 && (
          <div className="rounded border border-amber-300 bg-amber-50 px-2.5 py-1.5
                          dark:border-amber-800 dark:bg-amber-900/20">
            <p className="text-[11px] font-medium text-amber-800 dark:text-amber-200">
              Worth checking before you trust this
            </p>
            <ul className="mt-0.5 space-y-0.5">
              {capture.dataFlags!.map((f) => (
                <li key={`${f.field}-${f.kind}`} className="text-[11px] text-amber-800 dark:text-amber-200">
                  <span className="font-medium">{f.field}</span> — {f.message}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Kept, not dropped. The backend stores "" on purpose: the tool
            having fired and sent an empty value is a different fact from the
            tool never having asked, and only the first of the two is a
            question worth changing in the agent's script. They are listed as
            names rather than as empty rows so they do not bury the answers. */}
        {blank.length > 0 && (
          <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
            <span className="font-medium">Asked, no answer given:</span>{' '}
            {blank.map((f) => f.label).join(', ')}
          </p>
        )}

        {capture.notes && (
          <p className="text-[12px] text-gray-700 dark:text-gray-200">
            <span className="font-medium">Notes:</span> {capture.notes}
          </p>
        )}

        {/* Only when there is something to do about it. A green "synced to
            zoho, record 5477000000123" on every row is noise. */}
        {/* The reason, not the payload. This used to print the raw Zoho
            response — a reader was shown `{"code":"INVALID_DATA","details":
            {"expected_data_type":"phone","api_name":"Phone"}}` and left to work
            out what to do with it. The server now classifies it, so the screen
            can say which of two very different things happened: one that fixes
            itself, and one that needs a person. The raw error is kept behind a
            disclosure rather than dropped — it is the actual record. */}
        {capture.syncStatus === 'FAILED' && (
          <div className="text-[11px] text-red-600 dark:text-red-400 space-y-1">
            <p>
              Did not reach {capture.crmProvider || 'the CRM'}
              {capture.syncErrorReason ? `: ${capture.syncErrorReason}.` : '.'}
              {' '}The capture itself is safe — this row is the record of it.
            </p>
            {capture.syncErrorRemedy && <p className="font-medium">{capture.syncErrorRemedy}</p>}
            {capture.syncRetryable === false && (
              <p style={{ color: 'var(--text-muted)' }}>
                Sending it again unchanged would fail the same way, so it is not retried.
              </p>
            )}
            {capture.syncError && (
              <details>
                <summary className="cursor-pointer" style={{ color: 'var(--text-muted)' }}>
                  What the CRM actually said
                </summary>
                <code className="block mt-1 break-all" style={{ color: 'var(--text-muted)' }}>
                  {capture.syncError}
                </code>
              </details>
            )}
          </div>
        )}
        {capture.syncStatus === 'QUEUED' && (
          <div className="text-[11px] text-amber-700 dark:text-amber-300 space-y-1">
            <p>
              Waiting to reach {capture.crmProvider || 'the CRM'}
              {capture.syncErrorReason ? `: ${capture.syncErrorReason}.` : '.'}
              {' '}A retry is scheduled; the row flips once it lands.
            </p>
            {capture.syncErrorRemedy && <p className="font-medium">{capture.syncErrorRemedy}</p>}
          </div>
        )}

        <div className="flex items-center gap-3 flex-wrap pt-1 border-t border-gray-100 dark:border-gray-700">
          <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
            {capture.source || 'unknown source'}
            {/* Name AND id, matching the filter's options exactly. The id is
                what the API narrows on and what this line has always shown, so
                it stays visible rather than being replaced by a name that only
                resolves while /agents is reachable. */}
            {capture.agentId != null ? ` · ${agentLabel(capture.agentId, agentNames)}` : ''}
            {/* Only when there is one. Every row in the table today has
                did=null, and "line —" on all of them would read as a fault. */}
            {capture.did ? ` · line ${capture.did}` : ''}
            {capture.crmRecordId ? ` · ${capture.crmProvider || 'CRM'} record ${capture.crmRecordId}` : ''}
          </span>
          {/* Stated, not left blank. A missing call id on a row about a phone
              call reads as a loading bug otherwise. */}
          {/* Stated where the actions used to be, because it explains their
              absence. */}
          {!capture.callSid && (
            <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
              · not linked to a specific call (captured before the tools sent a call id)
            </span>
          )}
          {showContactLink && capture.contactId && (
            <>
              <Link
                to={`/crm/captures/contact/${encodeURIComponent(capture.contactId)}`}
                className="text-[11px] inline-flex items-center gap-1 text-indigo-600 dark:text-indigo-400 hover:underline"
                title="Every lead, booking and message captured from this caller, across all their calls"
              >
                <User className="w-3 h-3" />
                All their leads &amp; bookings
              </Link>
              {/* Two different questions, so two destinations: the captures are
                  what this person told us, the profile is who they are and
                  every other channel they reached us on. */}
              <Link
                to={`/user-directory/contacts/${encodeURIComponent(capture.contactId)}`}
                className="text-[11px] inline-flex items-center gap-1 text-indigo-600 dark:text-indigo-400 hover:underline"
              >
                <ExternalLink className="w-3 h-3" />
                Contact profile
              </Link>
            </>
          )}
        </div>
    </div>
  );
};

const CaptureCard: React.FC<{
  capture: Capture;
  open: boolean;
  onToggle: () => void;
  /** Hidden on the contact's own page — it is already the page you are on. */
  showContactLink: boolean;
  /** Agent names by id, so the row names the agent the same way the filter does. */
  agentNames: Map<number, string>;
  /** Opens the call this capture came from, in place. */
  onOpenCall: (callSid: string) => void;
  /** The call currently being fetched, so only that row shows a spinner. */
  openingCall: string | null;
  /** Saves the call's audio, without opening a player first. */
  onDownloadCall: (callSid: string) => void;
  /** The call currently being signed for download — same reason as above. */
  downloadingCall: string | null;
  /** Opens who the lead is, without leaving the queue. */
  onOpenContact: (contactId: string, label: string, subject: string) => void;
  /** Opens the edit form; absent for a role that may not edit. */
  onEdit?: (capture: Capture) => void;
}> = ({ capture, open, onToggle, showContactLink, agentNames, onOpenCall, openingCall,
        onDownloadCall, downloadingCall, onOpenContact, onEdit }) => {
  const type = captureTypeMeta(capture.captureType);
  const sync = syncMeta(capture.syncStatus);
  const subject = captureSubject(capture.captureType);
  const { answered, blank } = captureFieldRows(capture);
  const identity = captureIdentity(capture);
  const amends = amendedBookingRef(capture);

  return (
    <div className="tl-card !p-0 overflow-hidden">
      {/* The toggle and the contact button are siblings, not nested. The whole
          header used to be one <button>; putting a second one inside it would be
          invalid HTML, and browsers resolve that by dropping the inner control —
          the affordance would render and simply never fire. */}
      <div className="flex items-stretch">
      <button
        className="flex-1 min-w-0 text-left px-4 py-3 flex items-start gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/40"
        onClick={onToggle}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {/* The type first and always. Lead vs booking vs message is the
                distinction the whole screen exists to draw, so it leads the row
                rather than sitting at the end of it. */}
            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${type.tone}`}>
              {type.label}
            </span>
            <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {identity.headline}
            </span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${sync.tone}`}>{sync.label}</span>
            <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
              {formatCapturedAt(capture.capturedAt)}
            </span>
          </div>
          <div className="flex items-center gap-2 flex-wrap mt-0.5">
            {/* The number, under the name rather than instead of it. A capture
                with a name used to hide its phone entirely — and the phone is
                what you dial, what you search for, and what the CRM
                de-duplicates on. Absent when it would simply repeat the line
                above, which captureIdentity decides so this does not have to. */}
            {(capture.dataFlags?.length ?? 0) > 0 && (
              /* On the collapsed row too: a flag nobody expands to see is a
                 flag nobody acts on. */
              <span
                className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800
                           dark:bg-amber-900/40 dark:text-amber-200"
                title={capture.dataFlags!.map((f) => `${f.field}: ${f.message}`).join('\n')}
              >
                check {capture.dataFlags!.length === 1 ? 'a value' : `${capture.dataFlags!.length} values`}
              </span>
            )}
            {identity.secondary && (
              <span className="text-[12px] tabular-nums" style={{ color: 'var(--text-muted)' }}>
                {identity.secondary}
              </span>
            )}
            <span className="text-[12px]" style={{ color: 'var(--text-muted)' }}>
              {answered.length === 0
                ? 'No answers recorded'
                : `${answered.length} answer${answered.length === 1 ? '' : 's'}`}
              {blank.length > 0 ? ` · ${blank.length} left blank` : ''}
            </span>
            {amends && (
              <span className="text-[11px] inline-flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
                <Link2 className="w-3 h-3" />
                {/* Same column, different meaning: on a BOOKING externalRef is
                    that booking's OWN reference, so this label appears only on
                    the two types that amend one. */}
                {capture.captureType === 'BOOKING_CANCELLED' ? 'cancels' : 'changes'} booking {amends}
              </span>
            )}
          </div>
        </div>
      </button>

      {/* Always visible, not hidden behind the disclosure: "who is this?" is the
          first question a lead provokes, and it should not cost an expand.
          Absent when the capture has no contactId — the contact write failed and
          there is nothing to open — and on the contact's own page, where it
          would lead back to where you already are. */}
      {showContactLink && capture.contactId && (
        <button
          type="button"
          onClick={() => onOpenContact(capture.contactId!, captureHeadline(capture), subject)}
          className="shrink-0 px-3 flex items-center gap-1.5 border-l border-gray-100 dark:border-gray-700
                     text-indigo-600 dark:text-indigo-400 hover:bg-gray-50 dark:hover:bg-gray-700/40"
          /* Named for what it OPENS, not for the table it reads.
             It said "Contact", which is where the data comes from — the one
             thing a person working a queue of leads does not need told. What
             they want to know is that this is the lead, and now also what was
             said on the calls with them. */
          title={`${subject} — who ${captureHeadline(capture)} is, and the calls with them`}
        >
          <User className="w-4 h-4" />
          <span className="hidden sm:inline text-[11px]">{subject}</span>
        </button>
      )}
      </div>

      {/* Inline only where a side panel cannot go. Below lg the panel would be
          a modal in all but name, and the expanding row is the better shape
          there; above it the same body is rendered once, in the panel. */}
      {open && (
        <div className="lg:hidden px-4 pb-3 pt-2 border-t border-gray-100 dark:border-gray-700">
          <CaptureDetail
            capture={capture}
            agentNames={agentNames}
            showContactLink={showContactLink}
            onOpenCall={onOpenCall}
            openingCall={openingCall}
            onDownloadCall={onDownloadCall}
            downloadingCall={downloadingCall}
            onEdit={onEdit}
          />
        </div>
      )}

    </div>
  );
};

const Captures: React.FC = () => {
  const { user, currentOrganization } = useAuth();
  // One component, two routes. /crm/captures is the organisation's list;
  // /crm/captures/contact/:contactId is one person's history, which is the
  // grouping the data actually supports. Both narrow with the same filters —
  // the server takes them on either endpoint, and a filter that worked on one
  // list and silently did nothing on the other would be worse than not having
  // it there at all.
  const { contactId } = useParams<{ contactId?: string }>();

  // THE FILTERS LIVE IN THE URL, and page does not.
  //
  // This screen exists to answer a question somebody asked — "what did agent 24
  // take last Tuesday that never reached the CRM" — and the answer is worth
  // sending back to the person who asked. In the URL it survives a reload,
  // works with the back button, and pastes into a chat as itself. Held in
  // component state it is a screenshot.
  //
  // `page` stays out: page 4 of a filter set is not something anybody means to
  // share, and restoring it would land the reader on an empty list every time
  // the data had shrunk since the link was made.
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(
    () => filtersFromSearchParams((k) => searchParams.get(k), KNOWN_TYPES),
    [searchParams],
  );

  const [page, setPage] = useState(0);
  const [data, setData] = useState<CapturePage>(EMPTY_PAGE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  // The contact being peeked at. The label is the row's own headline, carried so
  // the modal opens with a name instead of a spinner.
  const [peek, setPeek] = useState<{ id: string; label: string; subject: string } | null>(null);

  const [agents, setAgents] = useState<AiAgent[] | null>(null);
  // Every agent id this session has actually seen in a capture, accumulated
  // rather than read off the current page. An agent that no longer appears in
  // /agents is only discoverable from its own rows, and those rows may be on
  // page 3 — so forgetting the ids on every page change would make the option
  // appear and disappear as the reader paged.
  const [seenAgentIds, setSeenAgentIds] = useState<number[]>([]);
  const [didProbe, setDidProbe] = useState<DidProbe | null>(null);

  // The call behind a lead, opened in place.
  //
  // AudioSessionModal needs a session row (it loads the call by call_log_id),
  // and a capture carries only the call id — so the row is fetched on click via
  // the callId filter. One request, only when somebody asks, and no navigation
  // away from the list they are working through.
  const [callSession, setCallSession] = useState<any | null>(null);
  const [openingCall, setOpeningCall] = useState<string | null>(null);
  const [callOpenError, setCallOpenError] = useState<string | null>(null);
  /**
   * The call opened from a lead — its audio, its conversation, or both.
   *
   * <p>`url` is nullable because the two are independent. A call can have audio
   * and no transcript (nobody said anything) or a transcript and no audio (the
   * upload from the switch failed), and refusing to open unless BOTH exist hid
   * conversations we were holding.
   */
  const [recording, setRecording] = useState<{ callSid: string; url: string | null } | null>(null);
  /** That call's turns. `null` means none was found, not "not loaded yet". */
  const [turns, setTurns] = useState<Array<Record<string, unknown>> | null>(null);

  const openCall = useCallback(async (callSid: string) => {
    setOpeningCall(callSid);
    setCallOpenError(null);
    try {
      const res = await sessionsService.getAudioSessions({ callId: callSid, perPage: 1 });
      const rows: any[] = (res as any)?.data?.data || (res as any)?.data?.content || [];
      if (rows.length) {
        setCallSession(rows[0]);
        return;
      }

      // No audio session — which is the NORMAL case, not the exception. Measured
      // in production: of 173 leads carrying a call id, 0 have an audio_sessions
      // row and 172 have an indexed recording. This button therefore never
      // opened anything and always reported the call as gone.
      //
      // The recording index answers the same question from the call id, and
      // agent_call_events holds what was said — so the fallback is the call
      // itself rather than a refusal.
      //
      // BOTH AT ONCE. They are independent and neither depends on the other, so
      // fetching them in series would put the dialog a round trip further away
      // for nothing. Each is caught on its own: a transcript that errors must
      // not cost the reader the audio, or the other way round.
      const [url, tx] = await Promise.all([
        recordingsService.getPlayableUrl(callSid).catch(() => null),
        agentCallsService.transcript(callSid).catch(() => null),
      ]);
      const got = tx && tx.success && tx.data ? (tx.data.turns || []) : null;

      if (url || (got && got.length > 0)) {
        setTurns(got);
        setRecording({ callSid, url });
        return;
      }
      setCallOpenError('No call detail, recording or transcript is held for that call.');
    } catch {
      setCallOpenError('Could not open that call.');
    } finally {
      setOpeningCall(null);
    }
  }, []);

  const [downloadingCall, setDownloadingCall] = useState<string | null>(null);

  /**
   * Save the call's audio, rather than only play it.
   *
   * <p>A FRESH url is minted, not `recording.url` with `dl=1` bolted onto it.
   * That token lives five minutes: a dialog left open while somebody reads the
   * lead beside it outlives it, and the download would 401 on a player that is
   * still sitting there happily paused.
   */
  const downloadRecording = useCallback(async (callSid: string) => {
    setDownloadingCall(callSid);
    setCallOpenError(null);
    try {
      const url = await recordingsService.resolveDownloadUrl(callSid, null);
      if (!url) {
        setCallOpenError('No recording is held for that call.');
        return;
      }
      recordingsService.startDownload(url, `recording-${callSid}.wav`);
    } catch {
      setCallOpenError('The recording could not be downloaded.');
    } finally {
      setDownloadingCall(null);
    }
  }, []);

  // Memoised on `filters`, which is itself memoised on the URL — so this object
  // is referentially stable between renders and is safe as an effect dependency.
  const query = useMemo(() => captureQueryParams(filters), [filters]);

  /** One page, from whichever of the two list endpoints this route is. */
  const fetchPage = useCallback(
    (q: any) => (contactId ? capturesService.forContact(contactId, q) : capturesService.list(q)),
    [contactId],
  );

  const load = useCallback(async () => {
    setLoading(true);
    const res = await fetchPage({ ...query, page, size: PAGE_SIZE });
    if (res.success && res.data) {
      setData(res.data);
      setError(null);
      const ids = res.data.content
        .map((c) => c.agentId)
        .filter((id): id is number => id !== null && id !== undefined && Number.isFinite(id));
      setSeenAgentIds((prev) => Array.from(new Set([...prev, ...ids])));
    } else {
      // The page is cleared on failure. Leaving the previous page's rows under
      // a new filter would show a list that contradicts the filter above it.
      setData(EMPTY_PAGE);
      // The server's refusals are written to be read — "did '12' is not a
      // number — give at least 4 digits in any format" tells the reader exactly
      // what to do next, and apiEnvelope has already lifted it out of whichever
      // key it arrived in. Replacing it with "Could not load captures" would
      // throw away the only useful thing in the response.
      setError(res.error || 'Could not load captures');
    }
    setLoading(false);
  }, [fetchPage, query, page]);

  useEffect(() => { load(); }, [load]);

  // The agent names, once. A failure here costs the NAMES and not the filter —
  // agentOptions still offers every id the captures mention, labelled by id.
  useEffect(() => {
    let alive = true;
    aiAgentsService.list().then((res) => {
      if (alive && res.success && Array.isArray(res.data)) setAgents(res.data);
    }).catch(() => { /* names are a nicety; ids are the contract */ });
    return () => { alive = false; };
  }, []);

  // WHY AN EMPTY DID SEARCH ASKS TWO MORE QUESTIONS.
  //
  // ?did=9228034177 returns zero rows and that is the correct answer: every
  // capture in the table has did=NULL, because they all predate the column. An
  // empty list on its own reads as "no calls came from that line" — a claim
  // about the phone line — when the truth is a claim about the database. So
  // when a specific-DID search finds nothing, the same filter set is counted
  // twice more (size=1, totals only): once with no DID filter, once with
  // did=none. If those two counts are equal, no capture in range has a DID at
  // all and no DID search could ever have matched. If they differ, some rows do
  // carry a DID and zero really does mean no match.
  //
  // Asked of the server rather than assumed, because this never resolves on its
  // own — agent 21's capture tool has no route/call_to in its schema, so its
  // rows can never carry a DID and the table stays permanently mixed.
  useEffect(() => {
    if (!isSpecificDid(filters) || loading || error || data.totalElements > 0) {
      setDidProbe(null);
      return;
    }
    let alive = true;
    const base = { ...query, did: undefined, page: 0, size: 1 };
    Promise.all([
      fetchPage(base),
      fetchPage({ ...base, did: DID_NONE }),
    ]).then(([all, noDid]) => {
      if (!alive || !all.success || !all.data || !noDid.success || !noDid.data) return;
      setDidProbe({ total: all.data.totalElements, noDid: noDid.data.totalElements });
    }).catch(() => { /* leaves the generic filtered message, which is not wrong */ });
    return () => { alive = false; };
  }, [filters, query, loading, error, data.totalElements, fetchPage]);

  // Changing ANY filter goes back to the first page. Staying on page 4 of a
  // filter with two pages shows an empty list that reads as "no bookings".
  const apply = useCallback((next: CaptureFilters) => {
    setSearchParams(filtersToSearchParams(next), { replace: true });
    setPage(0);
    setOpen(null);
  }, [setSearchParams]);

  const setFilter = useCallback(
    <K extends keyof CaptureFilters>(key: K, value: CaptureFilters[K]) =>
      apply({ ...filters, [key]: value }),
    [apply, filters],
  );

  // The DID box is typed into, so it is held locally and pushed to the URL on a
  // pause. Writing every keystroke straight to the URL would fire a request per
  // character and paint the server's "give at least 4 digits" refusal over a
  // number the reader is halfway through typing.
  // The keys offered come from the rows on screen, not from a fixed list.
  // Atmantan's leads carry primary_wellness_goal and lead_quality; the
  // hospital's carry purpose and disposition — one screen serves both, and a
  // hardcoded list would offer one tenant the other's fields and silently omit
  // its own. Keys already in the URL are kept in the list even when this page
  // has no row carrying them, so a shared link does not lose its own filter.
  const fieldKeyOptions = useMemo(() => {
    const keys = new Set<string>();
    for (const row of data.content) {
      for (const k of Object.keys(captureFields(row) || {})) {
        if (/^[A-Za-z0-9_]{1,64}$/.test(k)) keys.add(k);
      }
    }
    if (filters.fieldKey) keys.add(filters.fieldKey);
    // Array.from, not a spread: this tsconfig target refuses to iterate a Set.
    return Array.from(keys).sort();
  }, [data.content, filters.fieldKey]);

  const [fieldValueDraft, setFieldValueDraft] = useState(filters.fieldValue);
  useEffect(() => { setFieldValueDraft(filters.fieldValue); }, [filters.fieldValue]);

  const [didDraft, setDidDraft] = useState(filters.did);
  const didDraftRef = useRef(didDraft);
  didDraftRef.current = didDraft;
  // Keeps the box honest when the filter changes from anywhere else — a chip
  // being cleared, Clear all, or the back button.
  useEffect(() => { setDidDraft(filters.did); }, [filters.did]);
  useEffect(() => {
    if (didDraft === filters.did) return;
    const t = setTimeout(() => {
      if (didDraftRef.current !== filters.did) setFilter('did', didDraftRef.current);
    }, 450);
    return () => clearTimeout(t);
  }, [didDraft, filters.did, setFilter]);

  const needsAttention = useMemo(() => attentionCount(data.content), [data.content]);
  // Resolved from the page in hand rather than held separately: paging already
  // clears `open`, so a stale selection cannot survive into a page that no
  // longer contains it.
  const selected = useMemo(
    () => data.content.find((c) => c.id === open) ?? null,
    [data.content, open],
  );
  const agentNames = useMemo(() => agentsById(agents), [agents]);
  const agentChoices = useMemo(
    () => agentOptions(agents, seenAgentIds, filters.agentId),
    [agents, seenAgentIds, filters.agentId],
  );
  const anyFilter = hasAnyFilter(filters);

  /**
   * Who is offered the download.
   *
   * The same roles the endpoint enforces, so the button is not a promise the
   * server refuses. Reading leads on screen and walking out with the whole book
   * are different acts: the organisation's own administrators and the platform,
   * never an ordinary user and never an agent. operator_admin is business_admin
   * under the legacy spelling, which is what every real account here holds.
   */
  const role = (currentOrganization?.role || user?.role)?.toLowerCase();
  const mayExport = ['master_admin', 'platform_admin', 'business_owner',
                     'business_admin', 'operator_admin'].includes(role || '');
  // LeadWriteController's list, both spellings. Not BUSINESS_AGENT.
  const mayEditLeads = canEditLeads(role);
  const [editing, setEditing] = useState<Capture | null>(null);
  const [adding, setAdding] = useState(false);
  const onEdit = mayEditLeads ? (c: Capture) => setEditing(c) : undefined;

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const exportCsv = async () => {
    setExporting(true);
    setExportError(null);
    // The same params the list is showing, so the file matches the screen.
    const res = await capturesService.exportCsv(captureQueryParams(filters));
    if (!res.ok) setExportError(res.error);
    setExporting(false);
  };
  /**
   * Whether the filter CONTROLS are showing, below lg.
   *
   * Closed by default there and nowhere else: seven labelled controls, each one
   * a full row on a phone, is about 350px of chrome — more than a screen — and
   * the reader reaches the first lead by scrolling past every filter they did
   * not set.
   */
  const [filtersOpen, setFiltersOpen] = useState(false);

  const chips = useMemo(
    () => activeFilterChips(filters, agentNames),
    [filters, agentNames],
  );
  const why = useMemo(
    () => emptyReason(filters, anyFilter, didProbe),
    [filters, anyFilter, didProbe],
  );

  // Wider than it was. max-w-5xl is 1024px, which suited a single column of
  // cards; splitting that into a list AND a side panel has to leave both halves
  // usable. At 7xl the even split gives each about 600px — enough for a row to
  // spell out a name, a number and a timestamp without truncating, and enough
  // for the detail to lay a label beside its value. The cap stays, because
  // full-bleed rows on a very wide monitor are hard to read.
  return (
    <div className="p-6 max-w-7xl">
      {contactId && (
        <Link
          to="/crm/captures"
          className="text-[12px] inline-flex items-center gap-1 mb-2 text-indigo-600 dark:text-indigo-400 hover:underline"
        >
          <ArrowLeft className="w-3 h-3" /> All leads &amp; bookings
        </Link>
      )}

      <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          {contactId ? 'Leads & bookings from this caller' : 'Leads & Bookings'}
        </h1>
        <div className="flex items-center gap-2">
          {mayEditLeads && !contactId && (
            <button
              className="tl-btn tl-btn-primary tl-btn-sm"
              onClick={() => setAdding(true)}
              title="Add a lead by hand. It is kept in cpass only and is not sent to your CRM."
            >
              <Plus className="w-3.5 h-3.5" /> Add lead
            </button>
          )}
          {mayExport && !contactId && (
            <button
              className="tl-btn tl-btn-neutral tl-btn-sm"
              onClick={exportCsv}
              disabled={exporting || loading}
              title="Download the leads currently shown, with the filters above applied"
            >
              {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              {exporting ? 'Preparing…' : 'Download CSV'}
            </button>
          )}
          <button className="tl-btn tl-btn-secondary tl-btn-sm" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Refresh
          </button>
        </div>
      </div>
      {exportError && (
        <p className="text-[12px] mb-1" style={{ color: 'var(--danger-text)' }}>{exportError}</p>
      )}
      <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
        {contactId
          ? 'Every capture from this contact, newest first — each one a separate call, kept as it was said rather than merged into a single answer.'
          : 'Every lead, booking and message your voice agents took down, with the answers the caller gave and whether it reached your CRM.'}
      </p>

      {/* The gist, above everything the filters touch.

          It is deliberately OUTSIDE the tabs and OUTSIDE the filter bar: it
          summarises every capture this caller can see, not the current
          selection. A summary that moved with the filters would turn "500
          leads" into "12" the moment somebody narrowed to one agent, with
          nothing on screen explaining the drop — and it would be a second,
          differently-scoped answer to the question the list below already
          answers.

          Hidden on a contact's own page, where the subject is one person and a
          platform-wide count is noise. */}
      {/* HIDDEN while the summary gets a page of its own.
          The strip above the list was the first cut; it is being redesigned as
          a proper Lead Overview screen — with the status vocabulary spelled out
          rather than left as bar labels — so it does not ship here in the
          meantime. LeadSummary and leadGist stay in the tree, tested and
          working, and the endpoints behind them are already live. */}
      {false && !contactId && <LeadSummary />}

      {/* Tabs, because these are the screen's two subjects rather than two of
          its filters. The row below narrows what you are looking at; this
          chooses WHICH THING you are looking at, and giving both the same pill
          treatment buried that difference.

          Messages is a link, not a tab. It lives under /whatsapp/*, a different
          source that none of the filters below apply to — a tab that silently
          ignored the filter bar above it would teach people the controls are
          unreliable. */}
      <div className="flex items-end justify-between gap-3 border-b mb-3"
           style={{ borderColor: 'var(--border)' }}>
        <div className="flex items-end gap-1">
          {CAPTURE_TABS.map((t) => {
            const on = filters.captureType === t.captureType;
            return (
              <button
                key={t.id}
                type="button"
                aria-current={on ? 'page' : undefined}
                onClick={() => setFilter('captureType', t.captureType)}
                className="px-3 py-2 text-[13.5px] font-medium -mb-px border-b-2 transition"
                style={on
                  ? { borderColor: 'var(--accent)', color: 'var(--accent-text)' }
                  : { borderColor: 'transparent', color: 'var(--text-muted)' }}
              >
                {t.label}
              </button>
            );
          })}
        </div>
        <Link
          to="/whatsapp/chats"
          className="pb-2 text-[12px] inline-flex items-center gap-1 hover:underline"
          style={{ color: 'var(--text-muted)' }}
        >
          <MessageSquare className="w-3.5 h-3.5" />
          Messages
        </Link>
      </div>

      {/* One row, always open — ON DESKTOP.
          A "Filters" disclosure must not hide the fact that the list is
          narrowed, which is why these stayed open everywhere. But the CHIPS
          below already carry that job: they spell out every active filter and
          each removes itself, and they are NOT collapsed here. So what folds
          away on a phone is the seven empty controls, never the evidence that
          something is filtered.

          Closed by default below lg because open it is ~350px of chrome — more
          than a phone screen — between the heading and the first lead. */}
      <button
        type="button"
        className="lg:hidden tl-btn tl-btn-sm tl-btn-neutral w-full justify-between mb-2"
        onClick={() => setFiltersOpen((o) => !o)}
        aria-expanded={filtersOpen}
      >
        <span className="inline-flex items-center gap-2">
          <SlidersHorizontal className="w-3.5 h-3.5" />
          {/* The count, so a collapsed panel still says it is doing something.
              The chips say WHICH; this says THAT, on the control that hides
              them. */}
          Filters{chips.length > 0 ? ` · ${chips.length} active` : ''}
        </span>
        {filtersOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>
      <div className={`${filtersOpen ? 'flex' : 'hidden'} lg:flex items-end gap-2 flex-wrap mb-2`}>
        <label className="flex flex-col gap-1 w-full sm:w-auto">
          <span className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Agent</span>
          <select
            className="tl-input !rounded-md !py-1.5 !text-[12px] w-full sm:w-auto sm:min-w-[190px]"
            value={filters.agentId === null ? '' : String(filters.agentId)}
            onChange={(e) => setFilter('agentId', e.target.value === '' ? null : Number(e.target.value))}
          >
            <option value="">Any agent</option>
            {agentChoices.map((o) => (
              // Name AND id. Someone who knows this agent as "22" and someone
              // who knows it as "IH_26 — Inbound" both have to be able to find
              // it. An id with no name behind it says so rather than passing
              // itself off as a complete label.
              <option key={o.value} value={o.value}>
                {o.unresolved ? `${o.label} (name unknown)` : o.label}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 w-full sm:w-auto">
          <span className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>CRM</span>
          <select
            className="tl-input !rounded-md !py-1.5 !text-[12px] min-w-[150px]"
            value={filters.syncStatus}
            onChange={(e) => setFilter('syncStatus', e.target.value as any)}
          >
            <option value="">Any status</option>
            {SYNC_STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 w-full sm:w-auto">
          <span className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Line called</span>
          <input
            className="tl-input !rounded-md !py-1.5 !text-[12px] w-[170px]"
            placeholder="DID, or none"
            value={didDraft}
            onChange={(e) => setDidDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') setFilter('did', didDraft); }}
          />
        </label>

        {/* Filtering on what the agent actually captured. The pair is sent only
            when both halves are set — the server refuses one without the other,
            because a half-written filter coming back empty reads as "no such
            leads" rather than as an unfinished question. */}
        <label className="flex flex-col gap-1 w-full sm:w-auto">
          <span className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Field</span>
          <select
            className="tl-input !rounded-md !py-1.5 !text-[12px] min-w-[160px]"
            value={filters.fieldKey}
            onChange={(e) => {
              const key = e.target.value;
              // Clearing the key clears the value with it, so a value can never
              // linger without a key to apply it to.
              apply({ ...filters, fieldKey: key, fieldValue: key ? filters.fieldValue : '' });
            }}
          >
            <option value="">Any field</option>
            {fieldKeyOptions.map((k) => (
              <option key={k} value={k}>{k.replace(/_/g, ' ')}</option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 w-full sm:w-auto">
          <span className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Is</span>
          <input
            className="tl-input !rounded-md !py-1.5 !text-[12px] w-[150px] disabled:opacity-50"
            placeholder={filters.fieldKey ? 'value' : 'pick a field first'}
            disabled={!filters.fieldKey}
            value={fieldValueDraft}
            onChange={(e) => setFieldValueDraft(e.target.value)}
            onBlur={() => { if (fieldValueDraft !== filters.fieldValue) setFilter('fieldValue', fieldValueDraft); }}
            onKeyDown={(e) => { if (e.key === 'Enter') setFilter('fieldValue', fieldValueDraft); }}
          />
        </label>

        <label className="flex flex-col gap-1 w-full sm:w-auto">
          <span className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>From</span>
          <input
            type="date"
            className="tl-input !rounded-md !py-1.5 !text-[12px] w-full sm:w-auto"
            value={filters.startDate}
            onChange={(e) => setFilter('startDate', e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1 w-full sm:w-auto">
          <span className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Until</span>
          <input
            type="date"
            className="tl-input !rounded-md !py-1.5 !text-[12px] w-full sm:w-auto"
            value={filters.endDate}
            onChange={(e) => setFilter('endDate', e.target.value)}
          />
        </label>

        {/* The one-click way to ask the question only the sentinel can ask.
            Typed by hand it is an undiscoverable keyword; as a button it is the
            answer to "so where ARE the ones with no line recorded". */}
        <button
          className={`text-[11px] px-2.5 py-1.5 rounded-md border inline-flex items-center gap-1 transition ${
            filters.did.trim().toLowerCase() === DID_NONE
              ? 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-700'
              : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40'
          }`}
          style={filters.did.trim().toLowerCase() === DID_NONE ? undefined : { color: 'var(--text-muted)' }}
          onClick={() => setFilter('did', filters.did.trim().toLowerCase() === DID_NONE ? '' : DID_NONE)}
          title="Captures with no DID recorded — every row that predates the column"
        >
          <PhoneOff className="w-3 h-3" /> No line recorded
        </button>
      </div>

      {/* The active set, spelled out. The controls above already show their own
          values, but a date picker reading "2026-08-20" and a select reading
          "In CRM" do not add up to a sentence anybody reads as "these four
          things are hiding rows from you". Each chip removes only itself, so
          undoing one filter is never "clear everything and start again". */}
      {chips.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap mb-4">
          <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>Showing only:</span>
          {chips.map((c) => (
            <button
              key={c.key}
              className="text-[11px] px-2 py-0.5 rounded-full border border-indigo-200 bg-indigo-50 text-indigo-700
                         dark:border-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300
                         inline-flex items-center gap-1 hover:bg-indigo-100 dark:hover:bg-indigo-900/50"
              onClick={() => apply(withoutFilter(filters, c.key))}
              title={`Remove this filter`}
            >
              {c.label}
              <X className="w-3 h-3" />
            </button>
          ))}
          <button
            className="text-[11px] px-2 py-0.5 rounded-full underline"
            style={{ color: 'var(--text-muted)' }}
            onClick={() => apply(EMPTY_FILTERS)}
          >
            Clear all
          </button>
        </div>
      )}
      {chips.length === 0 && <div className="mb-4" />}

      {/* Caught here rather than sent. The server accepts a backwards range and
          answers zero, which on screen is indistinguishable from a real
          no-match. */}
      {isBackwardsRange(filters) && (
        <div className="mb-4 flex gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm
                        text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            The <span className="font-medium">Until</span> date is before the{' '}
            <span className="font-medium">From</span> date, so nothing can match.
          </span>
        </div>
      )}

      {error && (
        <div className="mb-4 flex gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm
                        text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /><span>{error}</span>
        </div>
      )}

      {/* Counted over the loaded page only, and it says so. The API sends no
          per-status totals, and a count captioned as if it covered everything
          would shrink when the reader paged forward. */}
      {/* Saved views over the ordinary filter object, so the chips, the empty
          states and clear-all keep working without knowing a view was used.
          Only the CRM ones are offered: "Needs attention" and "Callback due"
          were asked for too, and both need a lead to have an owner and a due
          time, which nothing in the schema carries yet. */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="text-[11px] uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
          Quick views
        </span>
        {CAPTURE_VIEWS.map((v) => {
          const active = isViewActive(v, filters);
          return (
            <button
              key={v.id}
              type="button"
              title={v.hint}
              aria-pressed={active}
              onClick={() => apply(active ? EMPTY_FILTERS : v.filters)}
              className={`text-[12px] px-2.5 py-1 rounded-full border transition-colors ${
                active
                  ? 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-200'
                  : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              {v.label}
            </button>
          );
        })}
      </div>

      {needsAttention > 0 && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm
                        text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
          <span className="font-medium">
            {needsAttention} of the {data.content.length} on this page {needsAttention === 1 ? 'has' : 'have'} not
            reached your CRM.
          </span>{' '}
          Nothing is lost — every one of them is recorded here in full, and queued ones land by
          themselves once the integration is working.
          {/* The count was already computed and shown; what was missing was a
              way to act on it without reconstructing the filter by hand. */}
          {' '}
          <button
            type="button"
            className="underline underline-offset-2 font-medium"
            onClick={() => apply({ ...EMPTY_FILTERS, syncStatus: 'FAILED' })}
          >
            Show the ones that failed
          </button>
        </div>
      )}

      {/* AN EMPTY LIST IS NOT ONE FACT, AND THIS IS WHERE THE DIFFERENCE SHOWS.
          The DID case is the one that matters: a filter that returns nothing
          because the COLUMN is empty looks exactly like a filter that returns
          nothing because no call came from that line, and only one of the two is
          a statement about the phone. See emptyReason in captureFilters.ts. */}
      {!loading && data.content.length === 0 && !error && (
        <div className="tl-card text-center py-10 px-4">
          {why.kind === 'DID_NEVER_RECORDED' ? (
            <>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                No capture has a line recorded yet — so no DID search can match one.
              </p>
              <p className="text-[12px] mt-1.5 max-w-lg mx-auto" style={{ color: 'var(--text-muted)' }}>
                All {why.inRange} capture{why.inRange === 1 ? '' : 's'} in this range{' '}
                {why.inRange === 1 ? 'was' : 'were'} taken before the DID was stored, so this is a
                gap in what was recorded rather than a fact about that number. Some agents will
                never record one: an agent whose capture tool does not send the route cannot report
                which line the call arrived on.
              </p>
              <button
                className="tl-btn tl-btn-secondary tl-btn-sm mt-3"
                onClick={() => setFilter('did', '')}
              >
                Show all {why.inRange} anyway
              </button>
            </>
          ) : why.kind === 'DID_NO_MATCH' ? (
            <>
              <p className="text-sm text-gray-500">No captures came in on that line.</p>
              <p className="text-[12px] mt-1.5 max-w-lg mx-auto" style={{ color: 'var(--text-muted)' }}>
                {why.withDid} capture{why.withDid === 1 ? '' : 's'} in this range{' '}
                {why.withDid === 1 ? 'has' : 'have'} a line recorded, and none of them is this
                number — so this is a real no-match, not a gap in the data.
              </p>
            </>
          ) : why.kind === 'BACKWARDS_RANGE' ? (
            <p className="text-sm text-gray-500">Nothing can match while the dates are the wrong way round.</p>
          ) : why.kind === 'FILTERED_OUT' ? (
            <>
              <p className="text-sm text-gray-500">No captures match these filters.</p>
              <p className="text-[12px] mt-1.5" style={{ color: 'var(--text-muted)' }}>
                {chips.length === 1 ? 'One filter is' : `${chips.length} filters are`} narrowing this
                list. Clearing {chips.length === 1 ? 'it' : 'them'} shows everything captured.
              </p>
              <button className="tl-btn tl-btn-secondary tl-btn-sm mt-3" onClick={() => apply(EMPTY_FILTERS)}>
                Clear all filters
              </button>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-500">Nothing captured yet.</p>
              <p className="text-[12px] mt-1" style={{ color: 'var(--text-muted)' }}>
                A row appears here when an agent's lead, booking or message tool fires during a call.
                Calls that ended without one are in Call Activity.
              </p>
            </>
          )}
        </div>
      )}

      {/* Compact list, detail beside it — above lg only.
          Below that the grid collapses and each row expands in place, which is
          what CaptureDetail's lg:hidden placement in the card handles. A panel
          on a phone is a modal wearing a different name, and the expanding row
          is the better shape there.

          The panel is driven by `open`, which is component state rather than a
          route, so changing selection cannot cost the filters, the page or the
          scroll position — which is the whole point of reading a queue this
          way. */}
      {/* Even halves. The rail was a fixed 24rem, which left the rows carrying
          slack they had no use for — every one of them truncates or wraps long
          before 800px — while the detail beside them had to stack each label
          above its value for want of 160px. The space is worth more on the
          side being read. */}
      <div className="lg:grid lg:grid-cols-2 lg:gap-4 lg:items-start">
        <div className="space-y-2 min-w-0">
          {data.content.map((c) => (
            <CaptureCard
              key={c.id}
              capture={c}
              open={open === c.id}
              onToggle={() => setOpen(open === c.id ? null : c.id)}
              showContactLink={!contactId}
              onOpenCall={openCall}
              openingCall={openingCall}
              onDownloadCall={downloadRecording}
              downloadingCall={downloadingCall}
              agentNames={agentNames}
              onOpenContact={(id, label, subject) => setPeek({ id, label, subject })}
              onEdit={onEdit}
            />
          ))}
        </div>

        {/* Sized against the SCROLLPORT, not the viewport.
            The app scrolls inside <main className="flex-1 overflow-y-auto">,
            which sits below a ~4.5rem header inside an h-screen/overflow-hidden
            shell, and adds lg:p-6 of its own. A panel capped at 100vh is
            therefore taller than the box it sticks in, and because it is sticky
            its overflowing bottom never scrolls into view — the last inch of a
            long capture was unreachable.

            The subtraction is header + that padding + this sticky offset. It
            degrades gently in both directions: a little too tall and the panel's
            own scrollbar still reaches everything, a little too short and there
            is a small gap under it. */}
        <aside
          className="hidden lg:block sticky top-4 rounded-lg border border-gray-200 dark:border-gray-700
                     bg-white dark:bg-gray-800 max-h-[calc(100vh-9rem)] overflow-y-auto overscroll-contain"
          aria-live="polite"
        >
          {selected ? (
            <div className="p-4">
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {captureIdentity(selected).headline}
                  </p>
                  <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                    {captureTypeMeta(selected.captureType).label} · {formatCapturedAt(selected.capturedAt)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(null)}
                  className="text-[11px] shrink-0 hover:underline"
                  style={{ color: 'var(--text-muted)' }}
                >
                  Close
                </button>
              </div>
              <CaptureDetail
                capture={selected}
                agentNames={agentNames}
                showContactLink={!contactId}
                onOpenCall={openCall}
                openingCall={openingCall}
                onDownloadCall={downloadRecording}
                downloadingCall={downloadingCall}
                width="medium"
                onEdit={onEdit}
              />
            </div>
          ) : (
            /* Said rather than left blank: an empty rail beside a full list
               reads as something that failed to load. */
            <p className="p-4 text-[12px]" style={{ color: 'var(--text-muted)' }}>
              Pick a row to read what was captured on that call — the list keeps
              its place while you do.
            </p>
          )}
        </aside>
      </div>

      {data.totalPages > 1 && (
        <div className="mt-4">
          {/* The shared control counts from 1; the API counts from 0. */}
          <Pagination
            currentPage={data.page + 1}
            totalPages={data.totalPages}
            onPageChange={(p) => { setPage(p - 1); setOpen(null); }}
            totalItems={data.totalElements}
            itemsPerPage={data.size || PAGE_SIZE}
          />
        </div>
      )}

      {contactId && data.content.length > 0 && (
        <p className="text-[11px] mt-4 inline-flex items-start gap-1" style={{ color: 'var(--text-muted)' }}>
          <CalendarClock className="w-3 h-3 mt-0.5 shrink-0" />
          Ordered by when each was captured. Two rows with different answers to the same question
          are not a conflict — they are the caller changing their mind between calls, which the
          merged contact record used to hide.
        </p>
      )}

      {/* Why nothing opened, when it did not. A button that silently does
          nothing reads as broken. */}
      {/* The call's audio, when the lead resolved to a recording rather than to a
          switch session.
          
          A DIALOG, not an inline block. It used to render here at the page root,
          after the table — so "inline beside the lead" actually meant the very
          bottom of the page, far from the row that was clicked and often below
          the fold. Opening it over the page puts it where the reader is looking
          and matches AudioSessionModal, which is what the other route opens. */}
      {recording && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
             onClick={() => { setRecording(null); setTurns(null); }}>
        {/* Wider than it was: it held a single audio bar, and now holds a
            conversation next to it. */}
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-2xl p-4
                        max-h-[85vh] overflow-y-auto"
             onClick={(e) => e.stopPropagation()}>
          {/* Wraps, and the id breaks. A call id is 36 characters; beside a
              Download and a Close on a phone it pushed the header wider than
              the dialog and put a sideways scrollbar on the page. */}
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <span className="text-sm font-medium min-w-0 break-all">
              Recording of call {recording.callSid}
            </span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => downloadRecording(recording.callSid)}
                disabled={downloadingCall === recording.callSid}
                title="Save this call's audio"
                className="inline-flex items-center gap-1.5 text-[12px] rounded-lg px-2.5 py-1.5
                           border border-gray-200 dark:border-gray-700
                           hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
              >
                {downloadingCall === recording.callSid
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <Download className="w-3.5 h-3.5" />}
                {downloadingCall === recording.callSid ? 'Preparing…' : 'Download'}
              </button>
              <button type="button" className="text-[12px] underline underline-offset-2"
                      onClick={() => { setRecording(null); setTurns(null); }}>
                Close
              </button>
            </div>
          </div>
          {/* The conversation, with the player IN it rather than above it —
              listening and reading are one task, and the shared viewer already
              takes the player as a slot. Same component the contact timeline
              uses, so a call reads identically wherever it is opened.

              AudioPlayer replaces the bare <audio controlsList="nodownload">
              that was here: it offers no native save menu at all, so the
              affordance that menu would have given — this url, which carries no
              `dl=1` and would open in a tab rather than save — simply is not
              there. The Download button above is the path that produces a file. */}
          {turns && turns.length > 0 ? (
            <TranscriptViewer
              transcriptJson={JSON.stringify(turns)}
              title="Conversation"
              containerClassName="bg-gray-50 dark:bg-gray-800/50 p-3 rounded-lg space-y-2"
              player={recording.url ? <AudioPlayer src={recording.url} autoPlay /> : undefined}
            />
          ) : (
            <>
              {recording.url && <AudioPlayer src={recording.url} autoPlay />}
              {/* Said, not left blank. An empty dialog under a call that clearly
                  happened reads as a load that failed. */}
              <p className="mt-3 text-[12px]" style={{ color: 'var(--text-muted)' }}>
                Nothing was transcribed for this call.
              </p>
            </>
          )}
        </div>
        </div>
      )}

      {callOpenError && (
        <div className="mt-3 text-[12px] rounded-lg px-3 py-2 flex items-center justify-between gap-3
                        bg-amber-50 text-amber-900 dark:bg-amber-900/20 dark:text-amber-200">
          <span>{callOpenError}</span>
          <button type="button" className="underline underline-offset-2" onClick={() => setCallOpenError(null)}>
            Dismiss
          </button>
        </div>
      )}

      {/* The call, in place. Same modal the AI Calls screen uses, so the
          transcript, the recording and the capture all read identically
          wherever somebody arrives from. */}
      {callSession && (
        <AudioSessionModal session={callSession} onClose={() => setCallSession(null)} />
      )}

      {/* Who the lead is, in place. Identity only — the profile is a click away
          inside it for the history, which costs a federated query nobody peeking
          asked to pay. */}
      {editing && (
        <LeadEditModal
          mode="edit"
          capture={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { load(); }}
        />
      )}
      {adding && (
        <LeadEditModal
          mode="create"
          onClose={() => setAdding(false)}
          onSaved={() => { load(); }}
        />
      )}

      {peek && (
        <ContactPeekModal
          contactId={peek.id}
          fallbackLabel={peek.label}
          subject={peek.subject}
          onClose={() => setPeek(null)}
        />
      )}
    </div>
  );
};

export default Captures;
